import { useEffect, useMemo, useState } from "react";
import { Checkbox, DatePicker, Flex, Space } from "antd";
import { CalendarOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import CustomLabel from "@/components/inputs/CustomLabel";
import CustomSelect from "@/components/inputs/CustomSelect";
import DigitsDatePicker from "@/components/inputs/DigitsDatePicker";
import { MONTH, DAYS } from "@/constants/day";

// Константы для типов
const CALENDAR_TYPES = {
  DAY: "day",
  MONTH: "month",
  YEAR: "year",
  MOMENT: "moment",
  DATE: "date",
};

const EMPTY_VALUE = "notFilled";

// Утилиты для работы с датами
const dateUtils = {
  parseValue: (value, type) => {
    if (!value) return null;
    if (type === CALENDAR_TYPES.YEAR) return dayjs(`${value}-01-01`);
    if (type === CALENDAR_TYPES.MOMENT) return dayjs(value);
    if (type === CALENDAR_TYPES.DATE) return dayjs(value);
    return value;
  },

  formatValue: (date, type) => {
    if (!date) return null;
    if (!dayjs.isDayjs(date)) return date;

    switch (type) {
      case CALENDAR_TYPES.DATE:
        return date.format("YYYY-MM-DD");
      case CALENDAR_TYPES.MOMENT:
        return new Date(date).toISOString();
      case CALENDAR_TYPES.YEAR:
        return Number(date.format("YYYY"));
      default:
        return date;
    }
  },
};

// Конфигурация для разных типов календарей
const pickerConfig = {
  [CALENDAR_TYPES.DAY]: {
    options: DAYS,
    placeholderSingle: "Выберите день",
  },
  [CALENDAR_TYPES.MONTH]: {
    options: MONTH,
    placeholderSingle: "Выберите месяц",
  },
  [CALENDAR_TYPES.YEAR]: {
    placeholderSingle: "Выберите год",
  },
  [CALENDAR_TYPES.MOMENT]: {
    placeholderSingle: "Выберите дату и время",
  },
  [CALENDAR_TYPES.DATE]: {
    placeholderSingle: "Выберите дату",
  },
};

// Компонент для селекта с днями/месяцами
const SelectPicker = ({ placeholder, options, value, onChange, disabled = false }) => (
  <CustomSelect
    placeholder={placeholder}
    value={value}
    allowClear
    showSearch
    options={options}
    onChange={onChange}
    style={{ width: "100%" }}
    suffixIcon={<CalendarOutlined />}
    disabled={disabled}
  />
);

// Компонент для одного пикера
const SinglePicker = ({ type, value, onChange, placeholder, style, disabled = false }) => {
  const config = pickerConfig[type];

  if (type === CALENDAR_TYPES.DAY || type === CALENDAR_TYPES.MONTH) {
    return (
      <SelectPicker
        placeholder={placeholder}
        options={config.options}
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
    );
  }

  if (type === CALENDAR_TYPES.YEAR) {
    return (
      <DatePicker.YearPicker
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        style={style}
        disabled={disabled}
      />
    );
  }

  return (
    <DigitsDatePicker
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      style={style}
      disabled={disabled}
    />
  );
};

const FilterCalendar = ({
  value,
  label,
  type,
  onSubmit,
  fromTo = true,
  allowEmpty = false,
}) => {
  const [isEmpty, setIsEmpty] = useState(value === EMPTY_VALUE);

  useEffect(() => {
    setIsEmpty(value === EMPTY_VALUE);
  }, [value]);

  // Парсинг значений
  const { parsedFrom, parsedTo, parsedSingle } = useMemo(() => {
    if (value === EMPTY_VALUE) {
      return {
        parsedFrom: null,
        parsedTo: null,
        parsedSingle: null,
      };
    }
    if (fromTo) {
      return {
        parsedFrom: dateUtils.parseValue(value?.from, type),
        parsedTo: dateUtils.parseValue(value?.to, type),
        parsedSingle: null,
      };
    }
    return {
      parsedFrom: null,
      parsedTo: null,
      parsedSingle: dateUtils.parseValue(value, type),
    };
  }, [value, type, fromTo]);

  // Обработчик изменений
  const handleChange = (dateFrom, dateTo) => {
    if (fromTo) {
      const result = {};
      if (dateFrom) result.from = dateUtils.formatValue(dateFrom, type);
      if (dateTo) result.to = dateUtils.formatValue(dateTo, type);
      onSubmit(result);
    } else {
      onSubmit(dateUtils.formatValue(dateFrom, type));
    }
  };

  const handleEmptyChange = (e) => {
    const checked = e.target.checked;
    setIsEmpty(checked);

    if (checked) {
      onSubmit?.(EMPTY_VALUE);
    } else {
      onSubmit?.(null);
    }
  };

  const renderRangePicker = () => (
    <Space.Compact style={{ width: "100%" }}>
      <SinglePicker
        type={type}
        value={parsedFrom}
        onChange={(date) => handleChange(date, parsedTo)}
        placeholder="от"
        style={{ width: "50%" }}
        disabled={isEmpty}
      />
      <SinglePicker
        type={type}
        value={parsedTo}
        onChange={(date) => handleChange(parsedFrom, date)}
        placeholder="до"
        style={{ width: "50%" }}
        disabled={isEmpty}
      />
    </Space.Compact>
  );

  const renderSinglePicker = () => (
    <SinglePicker
      type={type}
      value={parsedSingle}
      onChange={handleChange}
      placeholder={pickerConfig[type]?.placeholderSingle}
      style={{ width: "100%" }}
      disabled={isEmpty}
    />
  );

  return (
    <div className="custom-calendar-select">
      {label && <CustomLabel text={label} />}

      <Flex gap={8} align="center">
        <div style={{ width: "100%" }}>
          {fromTo ? renderRangePicker() : renderSinglePicker()}
        </div>

        {allowEmpty && (
          <Checkbox
            style={{ width: 165 }}
            checked={isEmpty}
            onChange={handleEmptyChange}
          >
            Не заполнено
          </Checkbox>
        )}
      </Flex>
    </div>
  );
};

export default FilterCalendar;
